<template>
  <form @submit.prevent="handleSubmit" class="space-y-6">
    <!-- Naslov proizvoda -->
    <div>
      <label for="product-name" class="block text-sm font-medium text-gray-300"
        >Naslov oglasa *</label
      >
      <div class="mt-1">
        <input
          id="product-name"
          v-model="formData.name"
          type="text"
          autocomplete="off"
          placeholder="Naziv proizvoda"
          class="appearance-none block w-full px-3 py-2 border border-gray-700 rounded-md shadow-sm placeholder-gray-500 bg-gray-700 text-white focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          :class="{ 'border-red-500': errors.name }"
          @blur="validateField('name')"
        />
        <p v-if="errors.name" class="mt-1 text-sm text-red-500">
          {{ errors.name }}
        </p>
      </div>
    </div>

    <!-- Kategorija -->
    <ProductCategoryDropdown
      v-model="formData.categoryId"
      :error="errors.categoryId"
      @blur="validateField('categoryId')"
      @update:modelValue="validateField('categoryId')"
    />

    <!-- Cena -->
    <div>
      <label for="product-price" class="block text-sm font-medium text-gray-300"
        >Cena (RSD) *</label
      >
      <div class="mt-1 relative rounded-md shadow-sm">
        <input
          id="product-price"
          v-model="formData.price"
          type="text"
          placeholder="0.00"
          autocomplete="off"
          class="appearance-none block w-full px-3 py-2 border border-gray-700 rounded-md shadow-sm placeholder-gray-500 bg-gray-700 text-white focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          :class="{ 'border-red-500': errors.price }"
          @blur="validateField('price')"
          @input="formatPrice"
        />
        <div
          class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
        >
          <span class="text-gray-400 sm:text-sm">RSD</span>
        </div>
        <p v-if="errors.price" class="mt-1 text-sm text-red-500">
          {{ errors.price }}
        </p>
      </div>
    </div>

    <!-- Opis -->
    <div>
      <label
        for="product-description"
        class="block text-sm font-medium text-gray-300"
        >Opis proizvoda *</label
      >
      <div class="mt-1">
        <textarea
          id="product-description"
          v-model="formData.description"
          rows="5"
          placeholder="Detaljan opis proizvoda..."
          class="appearance-none block w-full px-3 py-2 border border-gray-700 rounded-md shadow-sm placeholder-gray-500 bg-gray-700 text-white focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          :class="{ 'border-red-500': errors.description }"
          @blur="validateField('description')"
        ></textarea>
        <p v-if="errors.description" class="mt-1 text-sm text-red-500">
          {{ errors.description }}
        </p>
      </div>
    </div>

    <!-- Slike -->
    <div>
      <label class="block text-sm font-medium text-gray-300"
        >Slike proizvoda *</label
      >
      <div class="mt-2">
        <!-- Upload area - prikazuj samo ako ima manje od 12 slika -->
        <div
          v-if="formData.images.length < 12"
          class="flex justify-center px-6 pt-5 pb-6 border-2 border-gray-700 border-dashed rounded-md hover:border-gray-500 transition-colors cursor-pointer"
          @click="triggerFileInput"
          @dragover.prevent
          @drop.prevent="handleDrop"
        >
          <div class="space-y-1 text-center">
            <svg
              class="mx-auto h-12 w-12 text-gray-400"
              stroke="currentColor"
              fill="none"
              viewBox="0 0 48 48"
              aria-hidden="true"
            >
              <path
                d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            <div class="flex text-sm text-gray-400">
              <p class="pl-1">Prevucite slike ovde ili kliknite za odabir</p>
              <input
                ref="fileInput"
                type="file"
                accept="image/*"
                multiple
                class="sr-only"
                @change="handleFileChange"
              />
            </div>
            <p class="text-xs text-gray-500">PNG, JPG, GIF do 10MB</p>
            <p class="text-xs text-blue-400">
              {{ formData.images.length }}/12 slika
            </p>
          </div>
        </div>

        <!-- Poruka kada je dostignut maksimum -->
        <div
          v-else
          class="flex justify-center px-6 pt-5 pb-6 border-2 border-gray-600 border-dashed rounded-md bg-gray-800"
        >
          <div class="space-y-1 text-center">
            <svg
              class="mx-auto h-12 w-12 text-gray-500"
              stroke="currentColor"
              fill="none"
              viewBox="0 0 48 48"
              aria-hidden="true"
            >
              <path
                d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            <p class="text-sm text-gray-500">
              Dostigli ste maksimum od 12 slika
            </p>
            <p class="text-xs text-gray-500">
              Uklonite neku sliku da biste dodali novu
            </p>
            <p class="text-xs text-blue-400">
              {{ formData.images.length }}/12 slika
            </p>
          </div>
        </div>

        <p v-if="errors.images" class="mt-1 text-sm text-red-500">
          {{ errors.images }}
        </p>

        <!-- Prikaz slika - PROMENJENA STRUKTURA -->
        <div v-if="formData.images.length > 0" class="image-gallery mt-4">
          <div class="image-grid">
            <div
              v-for="(image, index) in formData.images"
              :key="index"
              class="image-item"
            >
              <!-- Slika sa rotacijom -->
              <div class="image-container">
                <img
                  :src="image.preview"
                  :style="{ transform: `rotate(${image.rotation}deg)` }"
                  alt="Preview"
                />

                <!-- Badge za glavnu sliku -->
                <div v-if="image.isMain" class="main-badge">Glavna</div>
              </div>

              <!-- Akcije -->
              <div class="image-actions">
                <div class="action-buttons">
                  <!-- Postavi kao glavnu -->
                  <button
                    v-if="!image.isMain"
                    type="button"
                    @click.stop="setMainImage(index)"
                    class="action-button blue"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="h-5 w-5"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"
                      />
                    </svg>
                  </button>

                  <!-- Rotiraj -->
                  <button
                    type="button"
                    @click.stop="rotateImage(index)"
                    class="action-button green"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="h-5 w-5"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z"
                        clip-rule="evenodd"
                      />
                    </svg>
                  </button>

                  <!-- Ukloni -->
                  <button
                    type="button"
                    @click.stop="removeImage(index)"
                    class="action-button red"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="h-5 w-5"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                        clip-rule="evenodd"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Submit dugme -->
    <div>
      <button
        type="submit"
        :disabled="isLoading"
        class="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-indigo-400 disabled:cursor-not-allowed"
      >
        <svg
          v-if="isLoading"
          class="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            class="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            stroke-width="4"
          ></circle>
          <path
            class="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
        {{ isLoading ? 'Obrada...' : 'Objavi oglas' }}
      </button>
    </div>
  </form>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from 'vue';
import { useProductForm } from '@/composables/useProductForm';
import ProductCategoryDropdown from '@/components/ProductCategoryDropdown.vue';

const MAX_IMAGES = 12; // Konstanta za maksimalnu broj slika

export default defineComponent({
  name: 'ProductForm',
  components: {
    ProductCategoryDropdown,
  },
  setup() {
    const fileInput = ref<HTMLInputElement | null>(null);
    const errors = ref<Record<string, string>>({});
    const touchedFields = ref<Set<string>>(new Set());

    const {
      formData,
      isLoading,
      error,
      successMessage,
      categories,
      loadCategories,
      addImage,
      removeImage,
      setMainImage,
      rotateImage,
      createProduct,
      validateForm,
    } = useProductForm();

    // Izračunavanje parent kategorija
    const parentCategories = computed(() => {
      const parents = categories.value.filter((category) => !category.parent);
      return parents;
    });

    // Funkcija za dobijanje child kategorija
    const getChildCategories = (parentId: string) => {
      return categories.value.filter(
        (category) => category.parent && category.parent.id === parentId
      );
    };

    // Rukovanje klikom na upload područje
    const triggerFileInput = () => {
      if (fileInput.value) {
        fileInput.value.click();
      }
    };

    // Rukovanje drag & drop
    const handleDrop = (event: DragEvent) => {
      if (event.dataTransfer?.files) {
        handleFiles(event.dataTransfer.files);
      }
    };

    // Rukovanje izborom fajlova
    const handleFileChange = (event: Event) => {
      const target = event.target as HTMLInputElement;
      if (target.files) {
        handleFiles(target.files);
      }
    };

    // Obrada fajlova sa proverom ograničenja
    const handleFiles = (files: FileList) => {
      const currentImageCount = formData.value.images.length;
      const filesToProcess = Array.from(files);

      // Proveravamo koliko slika možemo dodati
      const remainingSlots = MAX_IMAGES - currentImageCount;

      if (remainingSlots <= 0) {
        alert(`Možete imati maksimalno ${MAX_IMAGES} slika po oglasu.`);
        return;
      }

      if (filesToProcess.length > remainingSlots) {
        alert(
          `Možete dodati još samo ${remainingSlots} slika. Odabrane su prve ${remainingSlots} slika.`
        );
        // Uzimamo samo one slike koje možemo dodati
        filesToProcess.splice(remainingSlots);
      }

      filesToProcess.forEach((file) => {
        if (file.type.startsWith('image/')) {
          if (file.size <= 10 * 1024 * 1024) {
            // 10MB limit
            addImage(file);
          } else {
            alert(`Slika "${file.name}" je veća od 10MB i neće biti dodana.`);
          }
        } else {
          alert(`Fajl "${file.name}" nije slika i neće biti dodat.`);
        }
      });

      // Resetujemo input kako bi korisnik mogao ponovno izabrati iste slike
      if (fileInput.value) {
        fileInput.value.value = '';
      }

      // Validiramo polje slika ako je već bilo dodirnuto
      if (touchedFields.value.has('images')) {
        validateField('images');
      }
    };

    // Formatiranje cene
    const formatPrice = (event: Event) => {
      const target = event.target as HTMLInputElement;
      let value = target.value;

      // Uklonimo sve što nije broj ili tačka
      value = value.replace(/[^\d.]/g, '');

      // Uklonimo više tačaka
      const dots = value.match(/\./g);
      if (dots && dots.length > 1) {
        value = value.replace(/\./g, (match, index) =>
          index === value.indexOf('.') ? '.' : ''
        );
      }

      // Ograničimo decimale na 2
      if (value.includes('.')) {
        const parts = value.split('.');
        if (parts[1].length > 2) {
          parts[1] = parts[1].substring(0, 2);
          value = parts.join('.');
        }
      }

      formData.value.price = value;
    };

    // Validacija pojedinačnog polja
    const validateField = async (fieldName: string) => {
      touchedFields.value.add(fieldName);

      const result = await validateForm();
      if (!result.isValid && result.errors[fieldName]) {
        errors.value[fieldName] = result.errors[fieldName];
      } else {
        delete errors.value[fieldName];
      }
    };

    // Slanje forme
    const handleSubmit = async () => {
      // Označimo sva polja kao dodirnuta
      ['name', 'description', 'price', 'categoryId', 'images'].forEach(
        (field) => {
          touchedFields.value.add(field);
        }
      );

      const validation = await validateForm();
      if (!validation.isValid) {
        errors.value = validation.errors;
        return;
      }

      await createProduct();
    };

    // Učitavanje kategorija pri učitavanju komponente
    onMounted(() => {
      loadCategories();
    });

    return {
      fileInput,
      formData,
      errors,
      isLoading,
      error,
      successMessage,
      categories,
      parentCategories,
      getChildCategories,
      triggerFileInput,
      handleDrop,
      handleFileChange,
      removeImage,
      setMainImage,
      rotateImage,
      formatPrice,
      validateField,
      handleSubmit,
    };
  },
});
</script>

<style scoped>
/* Potpuno prepravljeni stilovi za galeriju slika */
.image-gallery {
  width: 100%;
  overflow: hidden;
}

.image-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  width: 100%;
}

@media (min-width: 640px) {
  .image-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (min-width: 768px) {
  .image-grid {
    grid-template-columns: repeat(4, 1fr);
  }
}

.image-item {
  position: relative;
  border-radius: 0.5rem;
  border: 1px solid #374151; /* border-gray-700 */
  overflow: hidden;
  width: 100%;
}

.image-container {
  position: relative;
  padding-bottom: 100%; /* aspect ratio 1:1 */
  background-color: #1f2937; /* bg-gray-800 */
  overflow: hidden;
}

.image-container img {
  position: absolute;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.2s;
}

.main-badge {
  position: absolute;
  top: 0.5rem;
  left: 0.5rem;
  background-color: #312e81; /* bg-indigo-900 */
  color: #c7d2fe; /* text-indigo-200 */
  font-size: 0.75rem;
  font-weight: bold;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
}

.image-actions {
  position: absolute;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s;
}

.image-item:hover .image-actions {
  opacity: 1;
}

.action-buttons {
  display: flex;
  gap: 0.5rem;
}

.action-button {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.25rem;
  border-radius: 9999px;
  color: white;
}

.action-button.blue {
  background-color: #2563eb; /* bg-blue-600 */
}

.action-button.blue:hover {
  background-color: #1d4ed8; /* bg-blue-700 */
}

.action-button.green {
  background-color: #059669; /* bg-green-600 */
}

.action-button.green:hover {
  background-color: #047857; /* bg-green-700 */
}

.action-button.red {
  background-color: #dc2626; /* bg-red-600 */
}

.action-button.red:hover {
  background-color: #b91c1c; /* bg-red-700 */
}
</style>
